import { SpotlightManager } from '@atlaskit/onboarding';
import {
  useAnalytics,
  DatabaseContainerType,
  getDatabaseContainerType,
} from '@databases/analytics';
import { ViewEventData } from '@databases/analytics/src/types';
import { useContextualMenu } from '@databases/contextual-menu';
import { useDatabaseContent } from '@databases/database-state';
import { useDialog } from '@databases/dialog';
import { doExportContent } from '@databases/export';
import { useCopyListener } from '@databases/export/copy';
import { addFlag, useFlaggingCallback } from '@databases/flags';
import { useLocale } from '@databases/intl';
import { messages } from '@databases/intl/messages';
import DatabaseLayout from '@databases/layout/DatabaseLayout';
import usePanel from '@databases/panel/usePanel';
import { useCallbackEffect, useKeyboardShortcut } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import {
  deselectAll,
  selectEntrySingle,
  setSoftSelection,
} from '@databases/redux/cellSelections/actions';
import {
  selectHasSelectedCells,
  selectIsEntireEntrySelected,
} from '@databases/redux/cellSelections/selectors';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import { loadDependenciesAsync } from '@databases/utils/database/field-types';
import {
  getDatabaseEntryOptional,
  isBrokenDatabase,
} from '@databases/utils/database/utils';
import { getFilteredValuesWithContext } from '@databases/utils/database/view/view';
import { useCollabManager } from '@databases/utils/hooks/useCollabManager';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { isStandalone } from '@databases/utils/utils';
import { messages as viewSettingsMessages } from '@databases/views/messages';
import useView from '@databases/views/useView';
import useViewPanel from '@databases/views/useViewPanel';
import { useViewUiState } from '@databases/views/useViewUiState';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { CLOSE_SHORTCUT } from '../../../shortcuts';
import { removeTextSelection } from '../../utils';
import { useScope } from '../Scope';
import { DatabaseActionKey } from '../database/DatabaseActions';

import DatabaseLayoutRenderer from './DatabaseLayoutRenderer';
import Toolbar from './Toolbar';

export interface DatabaseContentProps {
  contentId: string;
  isContentEditable?: boolean;
  isFieldsEditable?: boolean;
  actions?: DatabaseActionKey[];
}

/**
 * Renders a table that allows to edit database values using inline edit fields and to add fields and add entries to a
 * database.
 */
const DatabaseContent: FunctionComponent<DatabaseContentProps> = ({
  contentId,
  isContentEditable = false,
  isFieldsEditable = false,
  actions,
}) => {
  const { entryId } = useDatabaseContent();
  const analyticsClient = useAnalytics();
  const dispatch = defaultStoreUtils.useDispatch();
  const locale = useLocale();
  const { setActivePanelSetting } = useViewUiState();

  const { showPanel, hidePanel, isPanelVisible } = usePanel();
  const { closeViewSettingsPanel } = useViewPanel();
  const { closeMenu } = useContextualMenu();
  const store = defaultStoreUtils.useStore();
  const intl = useIntl();
  const { formatMessage } = intl;
  const { view, setViewId, resolvedView } = useView();

  const [isLoaded, setIsLoaded] = useState(false);
  const [outerContainerRef, setOuterContainerRef] = useState<HTMLElement | null>(null);

  const collabManager = useCollabManager(contentId);
  const wasCreatedThisSession = collabManager?.wasCreatedThisSession;

  const scope = useScope();
  const [database, operations, isLocked, hasSelection, isEntrySelected, entryExists] =
    defaultStoreUtils.useEqualSelector((state) => {
      const database = selectDatabaseByContentIdRequired(state, { contentId });

      const isEntrySelected = entryId
        ? selectIsEntireEntrySelected(state, {
            entryId,
          })
        : null;
      const entry = entryId
        ? database &&
          !isBrokenDatabase(database) &&
          getDatabaseEntryOptional(database, entryId)
        : null;
      return [
        database,
        database.operations,
        database.meta.isLocked,
        selectHasSelectedCells(state, { contentId, scope, viewSettings: view.settings }),
        isEntrySelected,
        !!entry,
      ] as const;
    });

  const handleExportDatabaseContent = useFlaggingCallback(async () => {
    await doExportContent(contentId, scope, view.settings, store.getState());
  }, [contentId, scope, store, view.settings]);

  useCopyListener({
    onCopy: handleExportDatabaseContent,
    isDisabled: !hasSelection,
  });

  useEffect(() => {
    if (isPanelVisible('DatabaseSetupPanel')) {
      hidePanel();
    }

    if (wasCreatedThisSession) {
      showPanel('DatabaseSetupPanel');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const database = selectDatabaseByContentIdRequired(store.getState(), { contentId });

    if (view.settings.filter) {
      Promise.all(
        getFilteredValuesWithContext(database, view.settings.filter).map(
          async (context) => {
            await loadDependenciesAsync(
              {
                contentId: context.database.contentId,
                field: context.field,
                entryId: context.entry.id,
                value: context.value,
              },
              store,
              intl
            );
          }
        )
      );
    }

    if (!isLoaded && entryId && view.settings && entryExists) {
      showPanel('EntryViewPanel', {
        entryId,
      });

      if (!isEntrySelected) {
        dispatch(
          selectEntrySingle({
            contentId,
            scope,
            viewSettings: view.settings,
            entryId,
          })
        );
      }
    } else if (entryId && !entryExists) {
      void addFlag({
        id: 'linked-database-entry-deleted',
        title: 'Linked entry is deleted',
        body: formatMessage(messages['snackbar-error.deleted-linked-entry']),
        type: 'info',
      });
    }
    setIsLoaded(true);
  }, [
    contentId,
    database,
    dispatch,
    entryExists,
    entryId,
    isEntrySelected,
    isLoaded,
    scope,
    store,
    view,
    formatMessage,
    showPanel,
    intl,
  ]);

  const canEditContent = isContentEditable && operations.updateEntries;
  const couldEditFields = isFieldsEditable && operations.updateMetadata;
  const canEditFields = couldEditFields && !isLocked;

  useCallbackEffect(
    useCallback(async () => {
      await analyticsClient.sendDatabaseContentViewedEvent({
        contentId,
        databaseContainer: isStandalone()
          ? DatabaseContainerType.STANDALONE_DATABASE
          : DatabaseContainerType.EMBED_DATABASE,
        locale,
      });
    }, [analyticsClient, contentId, locale])
  );

  useKeyboardShortcut(
    CLOSE_SHORTCUT,
    () => {
      if (isPanelVisible()) {
        // when we remove the view settings panel from the panel stack (i.e. the
        // top/visible panel is the view settings panel), we want to clear its settings
        if (isPanelVisible('ViewSettingsPanel')) {
          setActivePanelSetting(null);
        }
        hidePanel();
        dispatch(setSoftSelection({ value: false }));
      } else {
        removeTextSelection();
        dispatch(deselectAll());
        closeMenu();
      }
    },
    false
  );

  const { showDialog } = useDialog();
  const client = useAnalytics();

  const handleDeleteView = useCallback(
    (viewId: string, source: ViewEventData['source'], onCloseAdditional?: () => void) => {
      showDialog('ConfirmDialog', {
        heading: formatMessage(messages['toolbar-views.delete-view-heading']),
        body: <p>{formatMessage(messages['toolbar-views.delete-view-text'])}</p>,
        yesLabel: formatMessage(
          viewSettingsMessages['view-actions.delete-view-delete-button']
        ),
        noLabel: formatMessage(messages['toolbar-views.delete-view-cancel-button']),
        appearance: 'danger',
        onClose: (result) => {
          if (result) {
            void client.sendViewDeletedEvent({
              contentId,
              databaseContainer: getDatabaseContainerType(),
              source: source,
              viewId: viewId,
            });

            setViewId(resolvedView.id);
            if (view.id === viewId) {
              closeViewSettingsPanel();
            }
            dispatch(
              addChangeToDraft(contentId, scope, {
                type: DatabaseChangeType.DELETE_SAVED_VIEW,
                viewId: viewId,
              })
            );

            onCloseAdditional && onCloseAdditional();
          }
        },
      });
    },
    [
      showDialog,
      formatMessage,
      client,
      contentId,
      setViewId,
      resolvedView.id,
      view.id,
      dispatch,
      scope,
      closeViewSettingsPanel,
    ]
  );

  return (
    <SpotlightManager blanketIsTinted={false}>
      <DatabaseLayout
        outerContainerRef={outerContainerRef}
        view={
          <DatabaseLayoutRenderer
            outerContainerRef={outerContainerRef}
            setOuterContainerRef={setOuterContainerRef}
            contentId={contentId}
            canEditContent={canEditContent}
            couldEditFields={couldEditFields}
            canEditFields={canEditFields}
            isContentEditable={isContentEditable}
            isFieldsEditable={isFieldsEditable}
            handleDeleteView={handleDeleteView}
          />
        }
        toolbar={
          <Toolbar
            contentId={contentId}
            actions={actions}
            canEditFields={canEditFields}
            canEditContent={canEditContent}
            handleDeleteView={handleDeleteView}
          />
        }
      />
    </SpotlightManager>
  );
};

export default DatabaseContent;
